import Fluxxor from 'fluxxor';
import { STATUS_INITIAL, STATUS_PENDING, STATUS_SUCCESS, STATUS_ERROR } from '../../constants';
import addDays from 'date-fns/addDays';
import { createDateTime } from 'utils/dateUtils';
import lscache from 'lscache';

import { rateAppManager } from 'singletons';
import { TriggeringEvent } from '~/types/RateApp';
import formatDate from 'utils/formatDate';

export const constants = {
  FETCH_OFFER_REQUEST: 'FETCH_OFFER_REQUEST',
  FETCH_OFFER_REQUEST_SUCCESS: 'FETCH_OFFER_REQUEST_SUCCESS',
  FETCH_OFFER_REQUEST_FAILED: 'FETCH_OFFER_REQUEST_FAILED',
  FETCH_OFFER_REQUEST_FROM_ACCOUNT: 'FETCH_OFFER_REQUEST_FROM_ACCOUNT',
  FETCH_OFFER_REQUEST_FROM_TRIP_SUCCESS: 'FETCH_OFFER_REQUEST_FROM_TRIP_SUCCESS',
  FETCH_EXPERIENCE_LISTING: 'FETCH_EXPERIENCE_LISTING',
  FETCH_EXPERIENCE_LISTING_SUCCESS: 'FETCH_EXPERIENCE_LISTING_SUCCESS',
  FETCH_EXPERIENCE_LISTING_FAILED: 'FETCH_EXPERIENCE_LISTING_FAILED',
  FETCH_WL_FEE_SUCCESS: 'FETCH_WL_FEE_SUCCESS',
  FETCH_WL_FEE_FAILED: 'FETCH_WL_FEE_FAILED',
  UPDATE_OFFER_TITLE: 'UPDATE_OFFER_TITLE',
  UPDATE_OFFER_ONLINE: 'UPDATE_OFFER_ONLINE',
  UPDATE_OFFER_DESCRIPTION: 'UPDATE_OFFER_DESCRIPTION',
  UPDATE_OFFER_DURATION: 'UPDATE_OFFER_DURATION',
  UPDATE_OFFER_DATE_TIME: 'UPDATE_OFFER_DATE_TIME',
  UPDATE_OFFER_PRICE: 'UPDATE_OFFER_PRICE',
  UPDATE_OFFER_GUESTS: 'UPDATE_OFFER_GUESTS',
  UPDATE_OFFER_LISTS: 'UPDATE_OFFER_LISTS',
  UPDATE_EXPERIENCE: 'UPDATE_EXPERIENCE',
  UPDATE_FULLY_PERSONALIZED: 'UPDATE_FULLY_PERSONALIZED',
  UPDATE_MEETING_POINT: 'UPDATE_MEETING_POINT',
  SET_OFFER_REQUEST_DRAFT: 'SET_OFFER_REQUEST_DRAFT',
  CLEAR_OFFER_REQUEST_DRAFT: 'CLEAR_OFFER_REQUEST_DRAFT',
};

const EMPTY_ADDRESS = {
  city: null,
  components: {
    country: null,
    locality: null,
    postal_code: null,
  },
  country: null,
  formatted_address: null,
  location: {
    lat: 0,
    lon: 0,
  },
  street: null,
};

const EMPTY_TRIP_DETAILS = {
  description: '',
  exactSpot: null,
  included: [],
  meetOption: null,
  meeting_point: null,
  notIncluded: [],
  public_meeting_point: null,
  title: '',
  tripDuration: {
    days: 0,
    hours: 3,
    minutes: 0,
  },
};

const TODAY = new Date();
const DEFAULT_STATUS = 'PROPOSAL';

export const OfferRequestStore = Fluxxor.createStore({
  initialize() {
    this.offerRequests = new Map();

    this.wlFee = 0;
    this.serviceFeePercentage = 0;

    this.experiences = {
      id: null,
      status: STATUS_INITIAL,
      data: null
    };

    this.personalizedOffer = {
      id: null,
      status: STATUS_INITIAL,
      data: {
        address: { ...EMPTY_ADDRESS },
        date: null,
        experience_id: null,
        guest_id: null,
        host_id: null,
        number_of_adults: null,
        number_of_children: null,
        number_of_guest: null,
        price: null,
        status: DEFAULT_STATUS,
        time: null,
        trip_date: null,
        trip_details: EMPTY_TRIP_DETAILS,
        user_request_id: null,
      }
    };

    this.offerRequestDraft = JSON.parse(lscache.get('offerRequestDraft'));

    this.bindActions(
      constants.FETCH_OFFER_REQUEST, this.onFetchOfferRequest,
      constants.FETCH_OFFER_REQUEST_SUCCESS, this.onFetchOfferRequestSuccess,
      constants.FETCH_OFFER_REQUEST_FAILED, this.onFetchOfferRequestFailed,
      constants.FETCH_OFFER_REQUEST_FROM_ACCOUNT, this.onFetchOfferRequestFailed,
      constants.FETCH_OFFER_REQUEST_FROM_TRIP_SUCCESS, this.onFetchOfferRequestFromTripSuccess,
      constants.FETCH_WL_FEE_SUCCESS, this.onFetchWlFeeSuccess,
      constants.FETCH_WL_FEE_FAILED, this.onFetchWlFeeFailed,
      constants.FETCH_EXPERIENCE_LISTING, this.onFetchExperienceListing,
      constants.FETCH_EXPERIENCE_LISTING_SUCCESS, this.onFetchExperienceListingSuccess,
      constants.FETCH_EXPERIENCE_LISTING_FAILED, this.onFetchExperienceListingFailed,
      constants.UPDATE_OFFER_TITLE, this.onUpdateOfferTitle,
      constants.UPDATE_OFFER_ONLINE, this.onUpdateOfferOnline,
      constants.UPDATE_OFFER_DESCRIPTION, this.onUpdateOfferDescription,
      constants.UPDATE_OFFER_PRICE, this.onUpdateOfferPrice,
      constants.UPDATE_OFFER_GUESTS, this.onUpdateOfferGuests,
      constants.UPDATE_OFFER_DATE_TIME, this.onUpdateOfferDateTime,
      constants.UPDATE_OFFER_DURATION, this.onUpdateOfferDuration,
      constants.UPDATE_OFFER_DATE_TIME, this.onUpdateOfferDateTime,
      constants.UPDATE_OFFER_LISTS, this.onUpdateOfferLists,
      constants.UPDATE_EXPERIENCE, this.onUpdateExperience,
      constants.UPDATE_FULLY_PERSONALIZED, this.onUpdateFullyPersonalized,
      constants.UPDATE_MEETING_POINT, this.onUpdateMeetingPoint,
      constants.SET_OFFER_REQUEST_DRAFT, this.onSetOfferRequestDraft,
      constants.CLEAR_OFFER_REQUEST_DRAFT, this.onClearOfferRequestDraft,
    );
  },

  setPersonalizedOfferFromRequest(request) {
    const { parameters, experience } = request;

    let isFamilyFriendly = false;
    if (!!experience) {
      isFamilyFriendly = experience.isFamilyFriendly || false;
    }

    const personalizedOfferData = this.personalizedOffer.data;
    personalizedOfferData.date = formatDate(new Date(parameters.requestDate), 'yyyy-MM-dd');
    personalizedOfferData.time = formatDate(new Date(parameters.requestDate), 'HH:mm');
    personalizedOfferData.trip_date = formatDate(new Date(parameters.requestDate), "yyyy-MM-dd'T'HH:mm");
    personalizedOfferData.experience_id = request.experience_id;
    personalizedOfferData.guest_id = request.from;
    personalizedOfferData.host_id = request.to;
    personalizedOfferData.trip_details.title = experience?.title ?? '';
    personalizedOfferData.number_of_adults = parameters.requestPeople - parameters.requestChildren;
    personalizedOfferData.number_of_children = parameters.requestChildren;
    personalizedOfferData.number_of_guest = parameters.requestPeople;
    personalizedOfferData.trip_details.duration = parseInt(request?.duration ?? 3, 10);
    personalizedOfferData.price = parseFloat((request?.exp_based_price ?? 0).toFixed(2));
    personalizedOfferData.user_request_id = request?.id;
    personalizedOfferData.address = { ...this.setAddress(experience) };
    personalizedOfferData.trip_details.included = experience?.lists?.included ?? [];
    personalizedOfferData.trip_details.notIncluded = experience?.lists?.notIncluded ?? [];
    personalizedOfferData.trip_details.description = experience?.description;
    personalizedOfferData.isFamilyFriendly = parameters?.account?.isFamilyFriendly ?? isFamilyFriendly;

    this.personalizedOffer = {
      id: request.id,
      status: STATUS_SUCCESS,
      data: personalizedOfferData,
      fullyPersonalized: !!request.experience_id,
    };
  },

  onFetchOfferRequest({ requestId }) {
    this.offerRequests.set(requestId, {
      id: requestId,
      status: STATUS_PENDING,
      data: null
    });

    this.emit('change');
  },

  onFetchExperienceListing({ accountId }) {
    this.experiences = {
      id: accountId,
      status: STATUS_PENDING,
      data: null
    };

    this.emit('change');
  },

  onFetchOfferRequestSuccess({ requestId, response }) {
    rateAppManager.trigger(TriggeringEvent.PERSONALIZED_OFFER_RISED);

    this.offerRequests.set(requestId, {
      id: requestId,
      status: STATUS_SUCCESS,
      data: response,
    });
    this.setPersonalizedOfferFromRequest(response);
    this.emit('change');
  },

  onFetchWlFeeSuccess({ response }) {
    this.wlFee = response.feePercentage;
    this.serviceFeePercentage = response.serviceFeePercentage || 10;
    this.emit('change');
  },

  onFetchWlFeeFailed() {
    this.wlFee = 0
    this.emit('change');
  },

  onFetchOfferRequestFromTripSuccess({ requestId, response }) {
    this.offerRequests.set(requestId, {
      id: requestId,
      status: STATUS_SUCCESS,
      data: {
        trip_id: requestId,
        from: response.guest_id,
        to: response.host_id,
        parameters: {
          requestDate: response.trip_date,
          requestPeople: response.number_of_guest,
          requestChildren: response.number_of_children,
        },
        experience_id: response.experience_id,
        exp_based_price: response.total_price,
        wlFee: response.price.feePercentage,
        serviceFee: response.price.service_fee_amount.value,
        serviceFeePercentage: this.serviceFeePercentage,
      },
    });

    this.personalizedOffer = {
      id: requestId,
      status: STATUS_SUCCESS,
      fullyPersonalized: false,
      trip_id: requestId,
      data: {
        address: { ...this.setAddress(response.trip_details) },
        date: formatDate(new Date(response.trip_date), 'yyyy-MM-dd'),
        experience_id: response.experience_id,
        guest_id: response.guest_id,
        host_id: response.host_id,
        number_of_adults: response.number_of_adults,
        number_of_children: response.number_of_children,
        number_of_guest: response.number_of_guest,
        price: response.price.baseTotal ? response.price.baseTotal.value : response.price.total.value,
        status: DEFAULT_STATUS,
        time: formatDate(new Date(response.trip_date), 'HH:mm'),
        trip_date: response.trip_date,
        trip_details: { ...response.trip_details },
        user_request_id: null,
      }
    };

    this.emit('change');
  },

  onFetchExperienceListingSuccess({ accountId, response }) {
    this.experiences = {
      id: accountId,
      status: STATUS_SUCCESS,
      data: response,
    };

    if (this.personalizedOffer.data.experience_id) {
      const experience = response.find(exp => exp.id === this.personalizedOffer.data.experience_id);
      this.onUpdateExperience(experience);
    } else {
      this.emit('change');
    }
  },

  onFetchOfferRequestFailed({ requestId }) {
    const newOffer = {
      from: requestId,
      parameters: {
        requestDate: formatDate(addDays(TODAY, 1), 'yyyy-MM-dd') + 'T12:00',
        requestPeople: 4,
        requestChildren: 0,
      },
    };
    this.offerRequests.set(requestId, {
      id: requestId,
      status: STATUS_ERROR,
      data: newOffer,
    });
    this.setPersonalizedOfferFromRequest(newOffer);

    this.emit('change');
  },

  onFetchExperienceListingFailed({ accountId }) {
    this.experiences = {
      id: accountId,
      status: STATUS_ERROR,
      data: null,
    };

    this.emit('change');
  },

  onUpdateOfferTitle(title) {
    this.personalizedOffer.data.trip_details.title = title;
    this.emit('change');
  },

  onUpdateOfferOnline(isOnline) {
    this.personalizedOffer.data.trip_details.attributes = {
      ...this.personalizedOffer.data.trip_details.attributes,
      isOnline
    };

    this.personalizedOffer.data.trip_details.meeting_point = 'Online';

    this.personalizedOffer.data.address = {
      city: 'Online',
      components: {
        country: 'Online',
        locality: '',
        postal_code: '',
        street_number: '',
      },
      country: 'Online',
      formatted_address: 'Online',
      location: {
        lat: 0,
        lon: 0,
      },
      street: '',
    };

    this.emit('change');
  },

  onUpdateOfferDescription(description) {
    this.personalizedOffer.data.trip_details.description = description;
    this.emit('change');
  },

  onUpdateOfferGuests({ numberOfAdults, numberOfChildren }) {
    this.personalizedOffer.data.number_of_adults = numberOfAdults;
    this.personalizedOffer.data.number_of_children = numberOfChildren;
    this.personalizedOffer.data.number_of_guest = numberOfAdults + numberOfChildren;
    this.emit('change');
  },

  onUpdateOfferDateTime({ date, time }) {
    this.personalizedOffer.data.date = date;
    this.personalizedOffer.data.time = time;
    const datetimeObj = createDateTime(date, time);
    this.personalizedOffer.data.trip_date = formatDate(datetimeObj, "yyyy-MM-dd'T'HH:mm");
    this.emit('change');
  },

  onUpdateOfferDuration({ days, hours, minutes }) {
    this.personalizedOffer.data.trip_details.tripDuration = { days, hours, minutes };
    this.emit('change');
  },

  onUpdateOfferLists({ included, excluded }) {
    this.personalizedOffer.data.trip_details.included = included;
    this.personalizedOffer.data.trip_details.notIncluded = excluded;
    this.emit('change');
  },

  onUpdateOfferPrice(price) {
    this.personalizedOffer.data.price = parseFloat(parseFloat(price).toFixed(2));
    this.emit('change');
  },

  setAddress(experience) {
    const address = experience?.details?.address ?? experience?.address;
    return {
      city: address?.city ?? address?.components?.locality ?? '',
      components: {
        country: address?.components?.country,
        locality: address?.components?.locality ?? '',
        postal_code: address?.components?.postal_code ?? '',
        street_number: address?.components?.street_number ?? '',
      },
      country: address?.country,
      formatted_address: address?.formatted_address,
      location: {
        lat: address?.geometry?.location?.lat ?? 0,
        lon: address?.geometry?.location?.lon ?? 0,
      },
      street: address?.street ?? address?.formatted_address ?? ''
    };
  },

  onUpdateExperience(experience) {
    this.personalizedOffer.data.experience_id = experience.id;
    this.personalizedOffer.data.address = this.setAddress(experience);

    this.personalizedOffer.data.trip_details.description = experience.details.description;
    this.personalizedOffer.data.trip_details.exactSpot = experience.details.attributes.exactSpot;
    this.personalizedOffer.data.trip_details.included = experience.details.lists.included;
    this.personalizedOffer.data.trip_details.meetOption = experience.details.attributes.meetOption;
    this.personalizedOffer.data.trip_details.meeting_point = experience.details.attributes.meetingPoint;
    this.personalizedOffer.data.trip_details.privateMeetingPoint = experience.details.meetingPoint;
    this.personalizedOffer.data.trip_details.notIncluded = experience.details.lists.notIncluded || [];
    this.personalizedOffer.data.trip_details.public_meeting_point = experience.details.attributes.meetingPoint;
    this.personalizedOffer.data.trip_details.title = experience.details.title;
    this.personalizedOffer.data.trip_details.tripDuration = {
      days: parseInt(experience.details.attributes.duration_days, 10),
      hours: parseInt(experience.details.attributes.duration, 10),
      minutes: parseInt(experience.details.attributes.duration_minutes, 10),
    };

    if (!this.personalizedOffer.data.price) {
      const nrOfPeople = Math.min(this.personalizedOffer.data.number_of_guest, Object.keys(experience.details.pricing.searchable_price).slice(-1));
      const price = experience.details.pricing.searchable_price[nrOfPeople];
      this.personalizedOffer.data.price = parseFloat(price * this.personalizedOffer.data.number_of_guest.toFixed(2));
    }
    this.personalizedOffer.fullyPersonalized = false;

    this.emit('change');
  },

  onUpdateFullyPersonalized() {
    this.personalizedOffer.data.experience_id = null;
    this.personalizedOffer.data.address = { ...EMPTY_ADDRESS };
    this.personalizedOffer.data.trip_details.description = '';
    this.personalizedOffer.data.trip_details.included = [];
    this.personalizedOffer.data.trip_details.notIncluded = [];
    this.personalizedOffer.data.trip_details.title = '';
    this.personalizedOffer.data.trip_details.meeting_point = null;
    this.personalizedOffer.data.trip_details.public_meeting_point = null;
    this.personalizedOffer.fullyPersonalized = true;
    this.emit('change');
  },

  onUpdateMeetingPoint({ address, latLng, exactSpot, meetingPoint, meetOption }) {
    this.personalizedOffer.data.trip_details.exactSpot = exactSpot;
    this.personalizedOffer.data.trip_details.meeting_point = meetingPoint;
    this.personalizedOffer.data.trip_details.public_meeting_point = meetingPoint;
    this.personalizedOffer.data.trip_details.meetOption = meetOption;
    this.personalizedOffer.data.trip_details.meetingPoint = {
      exactSpot,
      meetOption,
      meeting_point: meetingPoint,
      public_meeting_point: meetingPoint,
    };

    this.personalizedOffer.data.address = {
      city: address.city || '',
      components: {
        country: address.country,
        locality: address.city || '',
        postal_code: address.postalcode || '',
        street_number: address.housenumber || '',
      },
      country: address.country,
      formatted_address: meetingPoint,
      location: {
        lat: latLng.lat || 0,
        lon: latLng.lng || 0,
      },
      street: address.street || '',
    };
    this.emit('change');
  },

  onClearOfferRequestDraft() {
    this.offerRequestDraft = null;
    lscache.remove('offerRequestDraft');
  },

  onSetOfferRequestDraft(draft) {
    this.offerRequestDraft = draft;
    lscache.set('offerRequestDraft', JSON.stringify(draft));
    this.emit('change');
  },

  getOfferRequest(id) {
    return this.offerRequests.get(id);
  },

  getWlFee() {
    return this.wlFee;
  },

  getServiceFeePercentage() {
    return this.serviceFeePercentage;
  },

  getExperiences() {
    return this.experiences;
  },

  getPersonalizedOffer() {
    return this.personalizedOffer;
  },

  getOfferRequestDraft() {
    return this.offerRequestDraft;
  }
});
